import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import { KPIS_QUERY_KEY } from './useKpis';
import { CAJAS_QUERY_KEY } from './useCajas';
import { CHART_QUERY_KEY } from './useChartData';
import { COBROS_PENDIENTES_QUERY_KEY } from './useCobrosPendientes';
import { EGRESOS_PENDIENTES_QUERY_KEY } from './useEgresosPendientes';
import type { Movimiento, CreateMovimientoPayload } from '../types';

export const MOVIMIENTOS_QUERY_KEY = ['contabilidad', 'movimientos'] as const;

const INVALIDATE_ALL = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: MOVIMIENTOS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: KPIS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: CHART_QUERY_KEY });
};

const INVALIDATE_DELETE = (queryClient: ReturnType<typeof useQueryClient>) => {
  INVALIDATE_ALL(queryClient);
  queryClient.invalidateQueries({ queryKey: COBROS_PENDIENTES_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: EGRESOS_PENDIENTES_QUERY_KEY });
};

export function useMovimientos() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<Movimiento[]>({
    queryKey: MOVIMIENTOS_QUERY_KEY,
    queryFn: () => contabilidadService.listMovimientos(),
    staleTime: 1000 * 30,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateMovimientoPayload) =>
      contabilidadService.createMovimiento(payload),
    onSuccess: () => INVALIDATE_ALL(queryClient),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateMovimientoPayload }) =>
      contabilidadService.updateMovimiento(id, payload),
    onSuccess: () => INVALIDATE_ALL(queryClient),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contabilidadService.deleteMovimiento(id),
    onSuccess: () => INVALIDATE_DELETE(queryClient),
  });

  return {
    movimientos: data ?? [],
    isLoading,
    error,
    createMovimiento: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateMovimiento: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteMovimiento: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
